import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styles from "./ClientQuotesTab.module.css";
import { quotesService, } from "@/api/services/quotes.service";
import { useToast } from "@/components/common";
import { useAlert } from "@/hooks/useAlert";
import { useAuth } from "@/context/AuthContext";
import { getRoutePrefix } from "@/utils/navigation";
import { Role } from "@/types/auth.types";
import { matchesQuoteStatus, getQuoteStatusForDisplay, } from "@/utils/quoteHelpers";
import { formatQuoteAddress } from "@/utils/address";
const FILTER_TABS = [
    { id: "draft", label: "Client Requested" },
    { id: "inprogress", label: "In Progress" },
    { id: "pending", label: "Pending Approval" },
    { id: "approved", label: "Approved" },
    { id: "declined", label: "Declined" },
    { id: "cancelled", label: "Cancelled" },
];
export const ClientQuotesTab = ({ companyName }) => {
    const { contactId } = useParams();
    const navigate = useNavigate();
    const { error: showError } = useToast();
    const { confirm } = useAlert();
    const { user } = useAuth();
    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeFilter, setActiveFilter] = useState("pending");
    const [isDeleting, setIsDeleting] = useState(null);
    const [isCopying, setIsCopying] = useState(null);
    const fetchQuotes = useCallback(async () => {
        if (!contactId && !companyName)
            return;
        try {
            setLoading(true);
            setError(null);
            // When a company name is available, load quotes using the same
            // search-based endpoint as the Estimations page so the counts
            // for each status (e.g. In Progress) align at the company level.
            if (companyName) {
                const response = await quotesService.getQuotes({
                    search: companyName,
                    pageSize: 1000,
                });
                setQuotes(response.data.data);
            }
            else {
                const response = await quotesService.getQuotesByContact(contactId);
                setQuotes(response.data);
            }
        }
        catch (err) {
            setError("Failed to load quotes");
            console.error("Error fetching quotes:", err);
        }
        finally {
            setLoading(false);
        }
    }, [companyName, contactId]);
    useEffect(() => {
        fetchQuotes();
    }, [fetchQuotes]);
    const filteredQuotes = quotes.filter((quote) => matchesQuoteStatus(quote.status, activeFilter));
    const handleEditQuote = (quoteId) => {
        // Navigate to quote edit page (estimating edit page)
        const routePrefix = getRoutePrefix(user?.role || Role.ADMIN);
        navigate(`${routePrefix}/estimating/${quoteId}/edit`);
    };
    const handleCopyQuote = async (quoteId) => {
        try {
            setIsCopying(quoteId);
            const response = await quotesService.copyQuote(quoteId);
            // Add the copied quote to the list
            setQuotes((prev) => [response.data, ...prev]);
            // Switch to inprogress filter to show the new copy (copies are created as inprogress)
            setActiveFilter("inprogress");
        }
        catch (err) {
            console.error("Error copying quote:", err);
            showError("Failed to copy quote");
        }
        finally {
            setIsCopying(null);
        }
    };
    const handleDeleteQuote = async (quoteId) => {
        const confirmed = await confirm("Are you sure you want to delete this quote?");
        if (!confirmed)
            return;
        try {
            setIsDeleting(quoteId);
            await quotesService.deleteQuote(quoteId);
            // Remove from local state
            setQuotes((prev) => prev.filter((q) => q.id !== quoteId));
        }
        catch (err) {
            console.error("Error deleting quote:", err);
            showError("Failed to delete quote");
        }
        finally {
            setIsDeleting(null);
        }
    };
    const getStatusLabel = (status) => {
        const labels = {
            draft: "REQUESTED",
            inprogress: "IN PROGRESS",
            pending: "PENDING APPROVAL",
            approved: "APPROVED",
            declined: "DECLINED",
            cancelled: "CANCELLED",
        };
        return labels[status] || status.toUpperCase();
    };
    const getStatusClass = (status) => {
        const classes = {
            draft: styles.statusDraft,
            inprogress: styles.statusInProgress,
            pending: styles.statusPending,
            approved: styles.statusApproved,
            declined: styles.statusDeclined,
            cancelled: styles.statusCancelled || styles.statusDeclined,
        };
        return classes[status] || "";
    };
    if (loading) {
        return (_jsx("div", { className: styles.container, children: _jsx("div", { className: styles.loadingState, children: "Loading quotes..." }) }));
    }
    if (error) {
        return (_jsx("div", { className: styles.container, children: _jsxs("div", { className: styles.errorState, children: [_jsx("p", { children: error }), _jsx("button", { onClick: fetchQuotes, className: styles.retryButton, children: "Retry" })] }) }));
    }
    return (_jsxs("div", { className: styles.container, children: [_jsx("div", { className: styles.filterTabs, children: FILTER_TABS.map((tab) => (_jsx("button", { className: `${styles.filterTab} ${activeFilter === tab.id ? styles.active : ""}`, onClick: () => setActiveFilter(tab.id), children: tab.label }, tab.id))) }), _jsx("div", { className: styles.quotesGrid, children: filteredQuotes.length === 0 ? (_jsxs("div", { className: styles.emptyState, children: [_jsx("svg", { width: "48", height: "48", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.5", children: _jsx("path", { d: "M9 12h6M12 9v6M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" }) }), _jsxs("p", { children: ["No ", activeFilter, " quotes found."] })] })) : (filteredQuotes.map((quote) => (_jsxs("div", { className: styles.quoteCard, children: [_jsxs("div", { className: styles.quoteLeftColumn, children: [_jsx("div", { className: styles.quoteImage, children: _jsx("img", { src: quote.imageUrl || "/images/Defit Social Media 3.png", alt: quote.title, onError: (e) => {
                                            e.target.src =
                                                "/images/Defit Social Media 3.png";
                                        } }) }), _jsx("button", { className: styles.editButton, onClick: () => handleEditQuote(quote.id), children: "EDIT QUOTE" }), _jsx("div", { className: styles.quoteStatus, children: _jsx("span", { className: `${styles.statusBadge} ${getStatusClass(getQuoteStatusForDisplay(quote.status))}`, children: getStatusLabel(getQuoteStatusForDisplay(quote.status)) }) })] }), _jsxs("div", { className: styles.quoteContent, children: [_jsxs("div", { className: styles.quoteInfo, children: [_jsx("h3", { className: styles.quoteTitle, children: quote.title }), _jsx("p", { className: styles.quoteAddress, children: formatQuoteAddress(quote, "No address specified") })] }), _jsx("div", { className: styles.quoteActions, children: _jsxs("div", { className: styles.iconActions, children: [_jsx("button", { className: styles.iconButton, onClick: () => handleCopyQuote(quote.id), title: "Copy quote", disabled: isCopying === quote.id, children: isCopying === quote.id ? (_jsx("span", { className: styles.spinner })) : (_jsxs("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("rect", { x: "9", y: "9", width: "13", height: "13", rx: "2", ry: "2" }), _jsx("path", { d: "M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" })] })) }), _jsx("button", { className: styles.deleteButton, onClick: () => handleDeleteQuote(quote.id), title: "Delete quote", disabled: isDeleting === quote.id, children: isDeleting === quote.id ? (_jsx("span", { className: styles.spinner })) : (_jsxs("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("circle", { cx: "12", cy: "12", r: "10" }), _jsx("line", { x1: "8", y1: "12", x2: "16", y2: "12" })] })) })] }) })] })] }, quote.id)))) })] }));
};
